'use client';

import type { FileContentResponse } from '@/lib/pr-v2/code-changes/types';
import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { fetchPrV2FileContent } from './fetch-pr-v2';
import { prV2FileContentQueryKey } from './query-keys';

export type PrV2FileContentParams = {
  filename: string;
  // Old-side path for renamed files (GitHub's previous_filename).
  previousFilename?: string | null;
  sha: string;
  status: string;
  // Comparison SHAs are mandatory: without them the /code-changes/file route falls back to the
  // canonical summary SHAs (potentially the wrong version under custom comparisons) and the
  // response would be cached forever (staleTime: Infinity) under a shared null-SHA query key,
  // serving stale contents after the PR gets new commits.
  headSha: string;
  baseSha: string;
};

// Loose variant used by the hook, whose `enabled` gate already blocks fetching until every
// param — including both comparison SHAs — is present.
type PrV2FileContentQueryParams = Omit<PrV2FileContentParams, 'headSha' | 'baseSha'> & {
  headSha?: string | null;
  baseSha?: string | null;
};

// Shared by the hook and ensurePrV2FileContent so both resolve to the same
// cache entry and dedupe in-flight fetches.
function prV2FileContentQueryOptions(
  owner: string,
  repo: string,
  number: string,
  params: PrV2FileContentQueryParams,
) {
  return {
    queryKey: prV2FileContentQueryKey(
      owner,
      repo,
      number,
      params.headSha,
      params.baseSha,
      params.filename,
      params.sha,
      params.previousFilename,
    ),
    queryFn: ({ signal }: { signal?: AbortSignal }): Promise<FileContentResponse> =>
      fetchPrV2FileContent(owner, repo, number, params, signal),
    // File content is keyed by blob sha, so it never goes stale.
    staleTime: Number.POSITIVE_INFINITY,
  };
}

export function usePrV2FileContent(
  owner: string,
  repo: string,
  number: string,
  options: {
    filename?: string | null;
    previousFilename?: string | null;
    sha?: string | null;
    status?: string | null;
    headSha?: string | null;
    baseSha?: string | null;
    enabled?: boolean;
  },
) {
  const { filename, previousFilename, sha, status, headSha, baseSha } = options;

  return useQuery({
    ...prV2FileContentQueryOptions(owner, repo, number, {
      filename: filename ?? '',
      previousFilename,
      sha: sha ?? '',
      status: status ?? '',
      headSha,
      baseSha,
    }),
    enabled:
      Boolean(owner && repo && number && filename && sha && status && headSha && baseSha) &&
      (options.enabled ?? true),
    refetchOnWindowFocus: false,
  });
}

/**
 * Imperative counterpart to usePrV2FileContent for click-time consumers.
 * Uses the same query key/fn, so it returns cached data when the hook already
 * fetched it and shares in-flight requests otherwise.
 */
export function ensurePrV2FileContent(
  queryClient: QueryClient,
  owner: string,
  repo: string,
  number: string,
  params: PrV2FileContentParams,
): Promise<FileContentResponse> {
  // Unlike the hook there is no `enabled` gate here, so also guard at runtime (for callers that
  // bypass the types): fetching without comparison SHAs would pin a server-side canonical-SHA
  // fallback response under the shared null-SHA key forever. Callers already catch and degrade.
  if (!params.headSha || !params.baseSha) {
    return Promise.reject(
      new Error('ensurePrV2FileContent requires headSha and baseSha to pin the file version'),
    );
  }

  return queryClient.fetchQuery(prV2FileContentQueryOptions(owner, repo, number, params));
}
